/**
 * Dense document library table (Documents tab). Built on the shared data-list
 * primitives. Type icon + filename open the source viewer; status shows
 * Ready/Indexing/Failed; the overflow cell deletes behind a ConfirmDialog.
 */
import { Badge } from "@d12labs/ui/badge";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { List, ListHeader, ListRow } from "@d12labs/ui/data-list";
import { Loader2, Trash2 } from "lucide-react";
import { DOC_TYPE_META, timeAgo } from "../kb-ui";
import type { KbDocument } from "../types";

const COLUMNS = [
	{ label: "Document", width: "1.7fr" },
	{ label: "Type", width: "120px" },
	{ label: "Chunks", width: "90px", align: "right" as const },
	{ label: "Updated", width: "100px" },
	{ label: "Status", width: "110px" },
	{ label: "", width: "36px" },
];

function StatusCell({ document }: { document: KbDocument }) {
	if (document.status === "ready") {
		return (
			<Badge tone="success" dot="var(--color-success)">
				Ready
			</Badge>
		);
	}
	if (document.status === "failed") {
		return <Badge tone="danger">Failed</Badge>;
	}
	return (
		<span className="inline-flex items-center gap-1.5 text-meta text-warning">
			<Loader2 className="size-3 animate-spin" />
			Indexing
		</span>
	);
}

export function DocumentTable({
	documents,
	onOpen,
	onDelete,
}: {
	documents: KbDocument[];
	onOpen: (doc: KbDocument) => void;
	onDelete: (id: string) => void;
}) {
	return (
		<List>
			<ListHeader columns={COLUMNS} />
			{documents.map((doc) => {
				const meta = DOC_TYPE_META[doc.docType];
				const TypeIcon = meta.icon;
				return (
					<ListRow
						key={doc.id}
						cells={[
							{
								width: "1.7fr",
								value: (
									<button
										type="button"
										onClick={() => onOpen(doc)}
										className="flex w-full items-center gap-2.5 text-left"
									>
										<span
											className="flex size-5 flex-none items-center justify-center"
											style={{ color: meta.tint }}
										>
											<TypeIcon className="size-4" />
										</span>
										<span className="truncate font-medium text-foreground hover:text-primary-hover">
											{doc.filename}
										</span>
									</button>
								),
							},
							{ width: "120px", value: meta.label, muted: true },
							{ width: "90px", align: "right", value: doc.chunkCount },
							{ width: "100px", value: timeAgo(doc.updatedAt), muted: true },
							{ width: "110px", value: <StatusCell document={doc} /> },
							{
								width: "36px",
								value: (
									<ConfirmDialog
										title="Delete document?"
										description={`"${doc.filename}" and its indexed chunks will be removed. This can't be undone.`}
										confirmLabel="Delete"
										destructive
										onConfirm={() => onDelete(doc.id)}
									>
										<button
											type="button"
											aria-label="Delete document"
											className="flex size-7 items-center justify-center rounded-md text-ink-subtle hover:bg-surface-2 hover:text-destructive"
										>
											<Trash2 className="size-3.5" />
										</button>
									</ConfirmDialog>
								),
							},
						]}
					/>
				);
			})}
		</List>
	);
}
